import { logger, parseDate } from '@icc/shared/helpers';
import { MerkleTrie } from './merkle';
import { MutableTimestamp, Timestamp } from './timestamp';

export type Clock = {
    timestamp: MutableTimestamp;
    merklesMap: Record<string, MerkleTrie>;
    lockedMap: Record<string, boolean>;
};

export type SerializedClock = {
    timestamp: string;
    merklesMap: Record<string, MerkleTrie>;
    lockedMap: Record<string, boolean>;
};

export class ClockService {
    static _clock: Clock;
    static serverGraphs: Record<string, MerkleTrie> = {};
    static serverModified: Record<string, Date> = {};
    static serverLastModified: Record<string, string> = {};
    static deletedDocs: string[] = [];
    static updatedGraphs: Record<string, number> = {};
    static updatedGraphsSent: Record<string, number> = {};
    static localLastOpened: Record<string, string> = {};

    static setClock(clock: Clock) {
        if (this._clock) {
            this._clock.merklesMap = clock.merklesMap;
            this._clock.timestamp = clock.timestamp;
        } else {
            this._clock = clock;
        }
    }

    static getClock() {
        return this._clock;
    }

    static makeClock(
        timestamp: MutableTimestamp,
        merklesMap: Record<string, MerkleTrie> = {},
        lockedMap: Record<string, boolean> = {},
    ): Clock {
        return { timestamp: MutableTimestamp.from(timestamp), merklesMap, lockedMap };
    }

    static serializeClock(clock: Clock): SerializedClock {
        return {
            timestamp: clock.timestamp.toString(),
            merklesMap: clock.merklesMap || {},
            lockedMap: clock.lockedMap || {},
        };
    }

    static deserializeClock(clock: SerializedClock): Clock | null {
        const timestamp = Timestamp.parse(clock.timestamp);
        if (timestamp) {
            return {
                timestamp: MutableTimestamp.from(timestamp),
                merklesMap: clock.merklesMap || {},
                lockedMap: clock.lockedMap || {},
            };
        }
        return null;
    }

    static updateGraphsSessionStore(
        graphs: Record<string, MerkleTrie>,
        modified: Record<string, string | Date>,
        lastModified: Record<string, string>,
        localLastOpened: Record<string, string[]>,
        deletedDocuments: string[]
    ) {
        Object.assign(this.serverGraphs, graphs);
        for (const documentId of Object.keys(modified)) {
            this.serverModified[documentId] = parseDate(modified[documentId]);
        }
        for (const documentId of Object.keys(lastModified)) {
            this.serverLastModified[documentId] = lastModified[documentId];
        }
        for (const documentId of Object.keys(localLastOpened)) {
            this.localLastOpened[documentId] = documentId;
        }
        for (const deletedDocument of deletedDocuments) {
            delete this.serverGraphs[deletedDocument];
        }
        logger.log('Updated graphs');
    }
}
